import { HttpClient } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { TranslatePipe } from '../../../core/pipes/translate.pipe';
import { AccountAchievementsSectionComponent } from './components/account-achievements-section';
import { AccountContactCardComponent } from './components/account-contact-card';
import { AccountGallerySectionComponent } from './components/account-gallery-section';
import { AccountProfileCardComponent } from './components/account-profile-card';
import { AccountReviewsSectionComponent } from './components/account-reviews-section';
import { AccountSectionCardComponent } from './components/account-section-card';
import { AccountTrustSafetyCardComponent } from './components/account-trust-safety-card';
import {
  AccountBadgeItem,
  AccountProfile,
  AccountReview,
  AccountStat,
  GalleryCategory,
} from './components/accounts-information.models';

interface AccountDetailItem {
  label: string;
  value: string;
  iconClass: string;
}

interface AccountDocumentItem {
  label: string;
  url: string;
  iconClass: string;
}

interface AccountPropertyCard {
  id: string;
  title: string;
  type: string;
  purpose: string;
  priceType: string;
  displayPrice: number;
  currency: string;
  area: number;
  bedrooms: number;
  bathrooms: number;
  furnished: boolean;
  isFeatured: boolean;
  isTrusted: boolean;
  managedByHomiGo: boolean;
  rating: number;
  createdAt: string;
  locationText: string;
  thumbnailUrl: string;
}

@Component({
  selector: 'app-accounts-information',
  standalone: true,
  imports: [
    CommonModule,
    TranslatePipe,
    AccountProfileCardComponent,
    AccountAchievementsSectionComponent,
    AccountGallerySectionComponent,
    AccountReviewsSectionComponent,
    AccountSectionCardComponent,
    AccountContactCardComponent,
    AccountTrustSafetyCardComponent,
  ],
  templateUrl: './accounts-information.html',
  styleUrl: './accounts-information.scss',
})
export class AccountsInformation implements OnInit, OnDestroy {
  isLoading = false;
  errorMessage = '';

  contactPhoneNumber = '';
  contactEmail = '';
  responseTime = '';
  rawProfile: any = null;
  accountDetails: AccountDetailItem[] = [];
  accountDocuments: AccountDocumentItem[] = [];
  specializations: string[] = [];
  certificatesAndLicenses: string[] = [];
  achievementsAndAwards: string[] = [];
  accountProperties: AccountPropertyCard[] = [];

  private routeSub?: Subscription;

  readonly defaultProfile: AccountProfile = {
    name: '',
    role: '',
    location: '',
    memberSince: '',
    avatarUrl: '',
    bio: '',
    rating: 0,
    reviewsCount: 0,
  };

  readonly defaultStats: AccountStat[] = [
    {
      label: 'accountsInformation.stats.properties',
      value: '0',
      containerClass: 'bg-primary-soft border-border-primary',
    },
    {
      label: 'accountsInformation.stats.successfulDeals',
      value: '0',
      containerClass: 'bg-surface-elevated border-border',
    },
    {
      label: 'accountsInformation.stats.successRate',
      value: '0%',
      containerClass: 'bg-success-soft border-success/20',
    },
    {
      label: 'accountsInformation.stats.rating',
      value: '0.0 (0)',
      containerClass: 'bg-surface-elevated border-border',
    },
  ];

  profile: AccountProfile = { ...this.defaultProfile };
  stats: AccountStat[] = [...this.defaultStats];
  achievements: AccountBadgeItem[] = [];
  galleryCategories: GalleryCategory[] = [];
  reviews: AccountReview[] = [];
  trustChecks: string[] = [];

  constructor(
    private route: ActivatedRoute,
    private http: HttpClient,
  ) { }

  ngOnInit(): void {
    this.routeSub = this.route.paramMap.subscribe((params) => {
      const role = params.get('role');
      const id = params.get('id');

      if (!role || !id) return;
      this.loadProfile(role, id);
    });
  }

  ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
  }

  get profileVisibilityText(): string {
    return this.mapVisibility(this.rawProfile?.ShowProfile) || 'accountsInformation.visibility.public';
  }

  private loadProfile(role: string, id: string): void {
    const endpoint = this.resolveEndpoint(role);

    this.isLoading = true;
    this.errorMessage = '';

    this.http.get<any>(`${environment.api.baseUrl}/${endpoint}/${id}`).subscribe({
      next: (response) => {
        this.applyProfileResponse(response);
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading account profile:', error);
        this.errorMessage = 'Unable to load the latest account data. Showing available details.';
        this.isLoading = false;
      },
    });
  }

  private resolveEndpoint(role: string): 'seeker' | 'developer' | 'owner' {
    const normalizedRole = role.toLowerCase();
    if (normalizedRole === 'seeker' || normalizedRole === 'property_seeker') return 'seeker';
    if (normalizedRole === 'developer' || normalizedRole === 'real_estate_developer') {
      return 'developer';
    }
    return 'owner';
  }

  private applyProfileResponse(response: any): void {
    const resProfile =
      response?.resProfile ||
      response?.developerProfile ||
      response?.ownerProfile ||
      response?.seekerProfile ||
      response?.profile ||
      response ||
      {};

    this.rawProfile = resProfile;

    const firstName = (resProfile.firstName || '').trim();
    const lastName = (resProfile.lastName || '').trim();
    const fullName = `${firstName} ${lastName}`.trim();
    const properties = this.getProfileProperties(resProfile);

    let avatarUrl =
      resProfile.profileImage ||
      resProfile.avatar ||
      resProfile.photo ||
      resProfile.image ||
      resProfile.logo ||
      '';

    if (avatarUrl && avatarUrl.startsWith('/')) {
      avatarUrl = `${environment.api.baseUrl}${avatarUrl}`;
    }

    this.profile = {
      ...this.defaultProfile,
      name: fullName || resProfile.name || resProfile.companyName || resProfile.username || '',
      role: this.mapRoleLabel(resProfile.role || resProfile.type),
      location: this.getLocationText(resProfile),
      memberSince: this.getMemberSince(resProfile),
      avatarUrl: avatarUrl,
      bio:
        resProfile.aboutMe ||
        resProfile.bio ||
        resProfile.about ||
        resProfile.description ||
        '',
      rating: this.toNumber(resProfile.averageRating ?? resProfile.rating, 0),
      reviewsCount: this.toNumber(resProfile.totalReviews ?? resProfile.reviewsCount, 0),
    };

    this.contactPhoneNumber = resProfile.phoneNumber || resProfile.phone || '';
    this.contactEmail = resProfile.email || '';
    this.responseTime = resProfile.responseTimeLabel || '';
    this.specializations = this.toStringArray(resProfile.specializations);
    this.certificatesAndLicenses = this.toStringArray(resProfile.certificatesAndLicenses);
    this.achievementsAndAwards = this.toStringArray(resProfile.achievementsAndAwards);
    this.accountProperties = properties.map((property: any) => this.mapPropertyCard(property));
    this.accountDetails = this.buildAccountDetails(resProfile);
    this.accountDocuments = this.buildAccountDocuments(resProfile);

    this.stats = this.buildStats(resProfile);
    this.achievements = this.buildAchievements(resProfile);
    this.galleryCategories = this.buildGalleryCategories(this.accountProperties);
    this.reviews = this.buildReviews(resProfile);
    this.trustChecks = this.buildTrustChecks(resProfile);
  }

  private mapRoleLabel(role: string | undefined): string {
    const normalized = (role || '').toLowerCase();
    if (normalized === 'real_estate_developer' || normalized === 'developer') return 'accountsInformation.roles.realEstateDeveloper';
    if (normalized === 'property_owner' || normalized === 'owner') return 'accountsInformation.roles.propertyOwner';
    if (normalized === 'property_seeker' || normalized === 'seeker') return 'accountsInformation.roles.propertySeeker';
    if (normalized === 'broker') return 'unitDetails.sidebar.broker';
    return role || '';
  }

  private mapVisibility(value: string | undefined): string {
    const normalized = (value || '').toLowerCase();
    if (normalized === 'public') return 'accountsInformation.visibility.public';
    if (normalized === 'registered') return 'accountsInformation.visibility.registered';
    if (normalized === 'private') return 'accountsInformation.visibility.private';
    return value || '';
  }

  private mapRegistrationPurpose(value: string | undefined): string {
    const normalized = (value || '').toLowerCase();
    if (normalized === 'rent') return 'accountsInformation.purpose.rent';
    if (normalized === 'sale') return 'accountsInformation.purpose.sale';
    if (normalized === 'both') return 'accountsInformation.purpose.both';
    return value || '';
  }

  private mapPurpose(value: string | undefined): string {
    const normalized = (value || '').toLowerCase();
    if (normalized === 'rent') return 'accountsInformation.purpose.rent';
    if (normalized === 'sale') return 'accountsInformation.purpose.sale';
    if (normalized === 'both' || normalized === 'sale_and_rent') {
      return 'accountsInformation.purpose.both';
    }
    return value || '';
  }

  private mapPriceType(value: string | undefined): string {
    const normalized = (value || '').toLowerCase();
    if (normalized === 'rent') return 'accountsInformation.priceType.rent';
    if (normalized === 'sale') return 'accountsInformation.priceType.sale';
    return value || '';
  }

  private getLocationText(profile: any): string {
    const primaryProperty = this.getProfileProperties(profile)[0];
    const area = profile.area || profile.city || primaryProperty?.location?.area || primaryProperty?.location?.city || '';
    const country = profile.country || primaryProperty?.location?.country || '';
    const location = [area, country].filter(Boolean).join(', ');
    return location || '';
  }

  private getMemberSince(profile: any): string {
    if (profile.memberSince) return profile.memberSince;
    if (profile.establishedYear) return String(profile.establishedYear);
    if (profile.createdAt) {
      const date = new Date(profile.createdAt);
      if (!Number.isNaN(date.getTime())) return date.toLocaleString('en-US', { month: 'long', year: 'numeric' });
    }
    return '';
  }

  private buildAccountDetails(profile: any): AccountDetailItem[] {
    return [
      { label: 'accountsInformation.details.email', value: profile.email, iconClass: 'fa-solid fa-envelope' },
      { label: 'accountsInformation.details.phone', value: profile.phoneNumber, iconClass: 'fa-solid fa-phone' },
      { label: 'accountsInformation.details.nationalId', value: profile.id, iconClass: 'fa-solid fa-id-card' },
      {
        label: 'accountsInformation.details.profileVisibility',
        value: this.mapVisibility(profile.ShowProfile),
        iconClass: 'fa-solid fa-eye',
      },
      {
        label: 'accountsInformation.details.registrationPurpose',
        value: this.mapRegistrationPurpose(profile.registrationPurpose),
        iconClass: 'fa-solid fa-bullseye',
      },
      { label: 'accountsInformation.details.responseTime', value: profile.responseTimeLabel, iconClass: 'fa-solid fa-clock' },
      {
        label: 'accountsInformation.details.trustStatus',
        value: profile.isTrusted
          ? 'accountsInformation.values.trusted'
          : 'accountsInformation.values.notTrustedYet',
        iconClass: profile.isTrusted ? 'fa-solid fa-shield-check' : 'fa-solid fa-shield-halved',
      },
    ].filter((item) => item.value !== null && item.value !== undefined && String(item.value).trim() !== '');
  }

  private buildAccountDocuments(profile: any): AccountDocumentItem[] {
    return [
      { label: 'accountsInformation.documents.idDocument', url: profile.idDocument, iconClass: 'fa-solid fa-file-shield' },
      { label: 'accountsInformation.documents.profileImage', url: profile.profileImage, iconClass: 'fa-solid fa-image' },
    ].filter((item) => Boolean(item.url));
  }

  private getProfileProperties(profile: any): any[] {
    const allItems = [
      ...(Array.isArray(profile.properties) ? profile.properties : []),
      ...(Array.isArray(profile.units) ? profile.units : [])
    ];
    
    // Remove duplicates based on _id or targetId
    const uniqueItems = Array.from(new Map(
      allItems.map(item => [item._id || item.targetId || Math.random().toString(), item])
    ).values());

    return uniqueItems;
  }

  private mapPropertyCard(property: any): AccountPropertyCard {
    return {
      id: property._id || property.targetId || '',
      title: property.title || property.title_en || property.title_ar || property.slug || 'accountsInformation.empty.untitledProperty',
      type: property.type || property.unitType || property.targetType || 'accountsInformation.labels.unit',
      purpose: this.mapPurpose(property.purpose),
      priceType: this.mapPriceType(property.priceType),
      displayPrice: this.toNumber(property.displayPrice, 0),
      currency: property.currency || 'EGP',
      area: this.toNumber(property.area || property.unitArea, 0),
      bedrooms: this.toNumber(property.bedrooms, 0),
      bathrooms: this.toNumber(property.bathrooms, 0),
      furnished: Boolean(property.furnished),
      isFeatured: Boolean(property.isFeatured),
      isTrusted: Boolean(property.isTrusted),
      managedByHomiGo: Boolean(property.managedByHomiGo),
      rating: this.toNumber(property.rating, 0),
      createdAt: property.createdAt || '',
      locationText: this.getPropertyLocationText(property),
      thumbnailUrl: property.thumbnail?.secure_url || property.gallery?.[0]?.secure_url || '',
    };
  }

  private getPropertyLocationText(property: any): string {
    const location = property.location || {};
    return [location.city, location.area, location.country].filter(Boolean).join(', ');
  }

  private buildGalleryCategories(properties: AccountPropertyCard[]): GalleryCategory[] {
    const photos = properties
      .filter((property) => property.thumbnailUrl)
      .map((property) => ({
        src: property.thumbnailUrl,
        alt: property.title,
      }));

    if (!photos.length) {
      return [];
    }

    return [
      {
        title: 'accountsInformation.gallery.listedProperties',
        iconClass: 'fas fa-building',
        photos,
      },
    ];
  }

  private buildReviews(profile: any): AccountReview[] {
    if (!Array.isArray(profile.reviews)) {
      return [];
    }

    return profile.reviews.map((review: any) => ({
      name: review.name || review.userName || 'accountsInformation.reviews.verifiedGuest',
      timeAgo: review.timeAgo || review.createdAt || '',
      rating: this.toNumber(review.rating, 0),
      comment: review.comment || review.review || '',
      propertyName: review.propertyName || review.unitTitle || '',
      avatarUrl: review.avatarUrl || review.profileImage || 'assets/default-avatar.png',
    }));
  }

  private buildTrustChecks(profile: any): string[] {
    const checks: string[] = [];
    if (profile.id || profile._id) checks.push('accountsInformation.trustChecks.nationalIdAvailable');
    if (profile.idDocument) checks.push('accountsInformation.trustChecks.identityDocumentUploaded');
    if (profile.ShowProfile) checks.push('accountsInformation.trustChecks.profileVisibilityConfigured');
    if (profile.responseTimeLabel) checks.push('accountsInformation.trustChecks.responseTimeAvailable');
    if (profile.isTrusted) checks.push('accountsInformation.trustChecks.trustedByPlatform');
    return checks;
  }

  private buildStats(profile: any): AccountStat[] {
    const propCount = profile.propertiesCount ?? profile.numberOfPropertiesOwned ?? this.accountProperties.length;
    const deals = profile.successfulDeals ?? profile.deals ?? 0;
    const successRate = this.toNumber(profile.successRate, -1);
    const successRateText = successRate >= 0 ? `${Math.max(0, Math.min(100, successRate))}%` : '0%';
    const avgRating = this.toNumber(profile.averageRating ?? profile.rating, 0);
    const totalRev = this.toNumber(profile.totalReviews ?? profile.reviewsCount, 0);

    return [
      {
        label: 'accountsInformation.stats.properties',
        value: String(propCount),
        containerClass: 'bg-primary-soft border-border-primary',
      },
      {
        label: 'accountsInformation.stats.successfulDeals',
        value: String(deals),
        containerClass: 'bg-surface-elevated border-border',
      },
      {
        label: 'accountsInformation.stats.successRate',
        value: successRateText,
        containerClass: 'bg-success-soft border-success/20',
      },
      {
        label: 'accountsInformation.stats.rating',
        value: `${avgRating.toFixed(1)} (${totalRev})`,
        containerClass: 'bg-surface-elevated border-border',
      },
    ];
  }

  private buildAchievements(profile: any): AccountBadgeItem[] {
    const dynamicItems: AccountBadgeItem[] = [];

    if (profile.companyName) {
      dynamicItems.push({
        title: 'accountsInformation.achievementTitles.company',
        description: profile.companyName,
        iconClass: 'fas fa-building',
      });
    }

    if (profile.commercialRegisterNumber) {
      dynamicItems.push({
        title: 'accountsInformation.achievementTitles.commercialRegistration',
        description: String(profile.commercialRegisterNumber),
        iconClass: 'fas fa-file-signature',
      });
    }

    this.toStringArray(profile.specializations).forEach((item) => {
      dynamicItems.push({
        title: 'accountsInformation.achievementTitles.specialization',
        description: item,
        iconClass: 'fas fa-layer-group',
      });
    });

    this.toStringArray(profile.certificatesAndLicenses).forEach((item) => {
      dynamicItems.push({
        title: 'accountsInformation.achievementTitles.certificateLicense',
        description: item,
        iconClass: 'fas fa-certificate',
      });
    });

    this.toStringArray(profile.achievementsAndAwards).forEach((item) => {
      dynamicItems.push({
        title: 'accountsInformation.achievementTitles.achievementAward',
        description: item,
        iconClass: 'fas fa-trophy',
      });
    });

    if (profile.isTrusted === true) {
      dynamicItems.push({
        title: 'accountsInformation.achievementTitles.trustedAccount',
        description: 'accountsInformation.achievementDescriptions.trustedAccount',
        iconClass: 'fas fa-shield-alt',
      });
    }

    const uniqueByTitle = dynamicItems.filter(
      (item, index, self) =>
        self.findIndex(
          (candidate) => candidate.title === item.title && candidate.description === item.description,
        ) === index,
    );

    return uniqueByTitle.slice(0, 8);
  }

  private toNumber(value: any, fallback: number): number {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : fallback;
  }

  private toOptionalNumberText(value: any, fallback: string): string {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue.toString() : fallback;
  }

  private toStringArray(value: any): string[] {
    return Array.isArray(value)
      ? value.filter((item) => item !== null && item !== undefined).map((item) => String(item))
      : [];
  }
}
